// ==================== SMS SERVICE ====================

import { axiosInstance as api } from './api';

// ==================== TYPES ====================

export type SmsProviderType = 
  // Pakistani Providers
  | 'EOCEAN' | 'JAZZ' | 'TELENOR' | 'UFONE' | 'ZONG' 
  | 'JEEO_SMS' | 'OUTREACH' | 'SMS4CONNECT' | 'BROADNET'
  // International Providers
  | 'TWILIO' | 'VONAGE' | 'MESSAGEBIRD' | 'PLIVO' | 'SINCH' | 'INFOBIP'
  // Custom
  | 'CUSTOM';

export interface SmsAccount {
  id: string;
  provider: SmsProviderType;
  senderId: string;
  baseUrl?: string;
  webhookVerifyToken?: string;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'DISCONNECTED';
  accountName?: string;
  accountBalance?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSmsAccountDto {
  provider: SmsProviderType;
  apiKey: string;
  apiSecret?: string;
  accountSid?: string;
  authToken?: string;
  senderId: string;
  baseUrl?: string;
}

export interface UpdateSmsAccountDto {
  provider?: SmsProviderType;
  apiKey?: string;
  apiSecret?: string;
  accountSid?: string;
  authToken?: string;
  senderId?: string;
  baseUrl?: string;
}

export interface SmsMessage {
  id: string;
  messageId?: string;
  direction: 'INBOUND' | 'OUTBOUND';
  content: string;
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'READ';
  segments: number;
  encoding?: string;
  sentAt?: string;
  deliveredAt?: string;
  createdAt: string;
  contact: {
    id: string;
    name?: string;
    phoneNumber: string;
  };
}

export interface SmsConversation {
  contactId: string;
  contactName?: string;
  contactPhone: string;
  lastMessage?: {
    content: string;
    direction: string;
    status: string;
    createdAt: string;
  };
  unreadCount: number;
  lastMessageAt: string;
}

export interface SendSmsDto {
  contactId?: string;
  phoneNumber?: string;
  content: string;
}

export interface ProviderConfig {
  provider: SmsProviderType;
  name: string;
  baseUrl: string;
  requiresSecret: boolean;
  requiresAccountSid: boolean;
  maxSenderId: number;
  country?: string;
}

// ==================== API FUNCTIONS ====================

// Helper to extract data from API response wrapper
interface ApiResponseWrapper<T> {
  success: boolean;
  message?: string;
  data?: T;
}

/**
 * Get SMS account configuration
 */
export const getSmsAccount = async (): Promise<SmsAccount | null> => {
  const response = await api.get('/customer/sms/account');
  const wrapper = response.data as ApiResponseWrapper<SmsAccount>;
  
  // Return null if no account exists (data is null, empty, or doesn't have an id)
  if (!wrapper.data || !wrapper.data.id) {
    return null;
  }
  return wrapper.data;
};

/**
 * Create SMS account configuration
 */
export const createSmsAccount = async (data: CreateSmsAccountDto): Promise<SmsAccount> => {
  const response = await api.post('/customer/sms/account', data);
  const wrapper = response.data as ApiResponseWrapper<SmsAccount>;
  return wrapper.data as SmsAccount;
};

/**
 * Update SMS account configuration
 */
export const updateSmsAccount = async (data: UpdateSmsAccountDto): Promise<SmsAccount> => {
  const response = await api.put('/customer/sms/account', data);
  const wrapper = response.data as ApiResponseWrapper<SmsAccount>;
  return wrapper.data as SmsAccount;
};

/**
 * Delete SMS account configuration
 */
export const deleteSmsAccount = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete('/customer/sms/account');
  const wrapper = response.data as ApiResponseWrapper<{ success: boolean; message: string }>;
  return wrapper.data || { success: wrapper.success, message: wrapper.message || '' };
};

/**
 * Test SMS provider connection
 */
export const testSmsConnection = async (): Promise<{
  success: boolean;
  message: string;
  data?: {
    accountName?: string;
    balance?: string;
  };
}> => {
  const response = await api.post('/customer/sms/test-connection');
  // Test connection returns the result directly with success property
  const wrapper = response.data as ApiResponseWrapper<{ accountName?: string; balance?: string }> | { success: boolean; message: string; data?: { accountName?: string; balance?: string } };
  
  // Check if it's already in the expected format (has success at top level from backend)
  if ('success' in wrapper && typeof wrapper.success === 'boolean' && 'message' in wrapper) {
    return wrapper as { success: boolean; message: string; data?: { accountName?: string; balance?: string } };
  }
  
  return {
    success: true,
    message: 'Connection successful',
    data: (wrapper as ApiResponseWrapper<{ accountName?: string; balance?: string }>).data,
  };
};

/**
 * Send SMS message
 */
export const sendSms = async (data: SendSmsDto): Promise<{
  id: string;
  messageId?: string;
  status: string;
  segments: number;
  encoding: string;
  sentAt?: string;
}> => {
  const response = await api.post('/customer/sms/send', data);
  const wrapper = response.data as ApiResponseWrapper<{
    id: string;
    messageId?: string;
    status: string;
    segments: number;
    encoding: string;
    sentAt?: string;
  }>;
  return wrapper.data as {
    id: string;
    messageId?: string;
    status: string;
    segments: number;
    encoding: string;
    sentAt?: string;
  };
};

/**
 * Get SMS messages for a contact
 */
export const getSmsMessages = async (
  contactId: string,
  page = 1,
  limit = 50
): Promise<{
  items: SmsMessage[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}> => {
  const response = await api.get('/customer/sms/messages', {
    params: { contactId, page, limit },
  });
  const wrapper = response.data as ApiResponseWrapper<{
    items: SmsMessage[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>;
  return wrapper.data || { items: [], meta: { total: 0, page: 1, limit, totalPages: 0 } };
};

/**
 * Get SMS conversations
 */
export const getSmsConversations = async (
  search?: string,
  page = 1,
  limit = 20
): Promise<{
  items: SmsConversation[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}> => {
  const response = await api.get('/customer/sms/conversations', {
    params: { search, page, limit },
  });
  const wrapper = response.data as ApiResponseWrapper<{
    items: SmsConversation[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>;
  return wrapper.data || { items: [], meta: { total: 0, page: 1, limit, totalPages: 0 } };
};

/**
 * Get available SMS providers
 */
export const getSmsProviders = async (): Promise<ProviderConfig[]> => {
  const response = await api.get('/customer/sms/providers');
  const wrapper = response.data as ApiResponseWrapper<ProviderConfig[]>;
  return wrapper.data || [];
};
